import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Instagram, Linkedin, MessageCircle } from 'lucide-react';
import { SocialButton } from './SocialButton';
interface Slide {
  id: number;
  title?: string;
  subtitle?: string;
  content: string;
  image: string;
  layout: 'text-left' | 'text-right';
  tools?: string[];
}
export function PortfolioSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const slides: Slide[] = [{
    id: 1,
    content: 'Over 7 years designing and managing community-driven programs to retool young people, educators, and organizations globally, for a changing world.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eCtAxeEcU6oApBA7i44hCT/dreyyyy.jpg',
    layout: 'text-left'
  }, {
    id: 2,
    title: 'TEDx Speaker & GIZ/GOPA Trained Trainer, Facilitator & Coach',
    content: 'Delivering thought leadership, impactful public speaking, and global-standard entrepreneurship training across Africa and beyond.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ugFbTTDzyr4U93hvHgnJ8i/1U0A9534.jpg',
    layout: 'text-right'
  }, {
    id: 3,
    title: 'Instructional & Learning Experience Designer',
    content: 'Specialized in AI-driven, future-ready learning design for individuals, organizations, and communities.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rwav8Fv9dJWaPhPTB17Yua/KLV_6500.jpg',
    layout: 'text-right',
    tools: ['Canva', 'Adobe Creative Suite', 'Figma', 'Miro', 'ChatGPT', 'Articulate', 'Notion']
  }, {
    id: 4,
    title: 'Community Builder & Activator',
    content: 'Founder of GoABitFurther Africa & Curator of The Networking Salon — building professional communities across Africa and globally.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iajYGVp5M8Eory23t1An2d/IMG_7302.jpg',
    layout: 'text-left'
  }, {
    id: 5,
    title: 'Retooling Individuals, Educators, and Organizations to leverage Artificial Intelligence Digital Media',
    content: 'Facilitating sessions for individuals, educators, and organizations, helping to save costs, drive efficiency, and create lasting impact with digital and AI tools.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6hn4yF9T5S5J8RxkrWpQad/IMG_0187.jpg',
    layout: 'text-right'
  }];
  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentSlide(prev => (prev + 1) % slides.length);
  }, [slides.length]);
  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      nextSlide();
    }, 7000);
    return () => clearInterval(interval);
  }, [nextSlide, isAutoPlaying]);
  const pauseAutoPlay = () => setIsAutoPlaying(false);
  const resumeAutoPlay = () => setIsAutoPlaying(true);
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0
    })
  };
  const renderTools = (tools: string[]) => <div className="grid grid-cols-2 gap-4 max-w-xs" data-id="element-127">
      {tools.map((tool, index) => <motion.div key={tool} className="flex flex-col items-center p-3 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: index * 0.1,
      duration: 0.5
    }} data-id="element-128">
          <span className="text-sm font-medium text-gray-800" data-id="element-129">{tool}</span>
        </motion.div>)}
    </div>;
  const SocialButtons = () => <motion.div className="flex flex-row space-x-3 mt-6" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6,
    delay: 0.6
  }} data-id="element-130">
      <SocialButton href="https://wa.me/2348127476613" label="WhatsApp" icon={<MessageCircle size={18} data-id="element-132" />} primary data-id="element-131" />
      <SocialButton href="https://linkedin.com/in/andrewigbineweka" label="LinkedIn" icon={<Linkedin size={18} data-id="element-134" />} data-id="element-133" />
      <SocialButton href="https://instagram.com/andrewigbineweka" label="Instagram" icon={<Instagram size={18} data-id="element-136" />} data-id="element-135" />
    </motion.div>;
  return <section className="h-screen w-full relative overflow-hidden bg-gray-50 pt-16" data-id="element-137">
      <div className="absolute inset-0 z-10 mt-16" data-id="element-138">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-139">
          <motion.div key={currentSlide} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
          x: {
            type: 'spring',
            stiffness: 300,
            damping: 30
          },
          opacity: {
            duration: 0.5
          }
        }} className="absolute inset-0 w-full h-full" onHoverStart={pauseAutoPlay} onHoverEnd={resumeAutoPlay} data-id="element-140">
            <div className="w-full h-full flex flex-col md:flex-row items-center" data-id="element-141">
              {slides[currentSlide].layout === 'text-left' ? <>
                  <div className="w-full md:w-1/2 h-full flex items-center justify-center p-8 md:p-16" data-id="element-142">
                    <div className="max-w-xl" data-id="element-143">
                      {slides[currentSlide].title && <motion.h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-gray-900" initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.6,
                    delay: 0.2
                  }} data-id="element-144">
                          {slides[currentSlide].title}
                        </motion.h1>}
                      <motion.p className="text-xl md:text-2xl lg:text-3xl font-light mb-8 text-gray-800 leading-relaxed" initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.6,
                    delay: 0.4
                  }} data-id="element-145">
                        {slides[currentSlide].content}
                      </motion.p>
                      {slides[currentSlide].tools && renderTools(slides[currentSlide].tools)}
                      <SocialButtons data-id="element-146" />
                    </div>
                  </div>
                  <div className="w-full md:w-1/2 h-full relative" data-id="element-147">
                    <motion.div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-transparent z-10 md:block hidden" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  duration: 0.8
                }} data-id="element-148" />
                    <motion.div className="w-full h-full" initial={{
                  scale: 1.1
                }} animate={{
                  scale: 1
                }} transition={{
                  duration: 1.5
                }} data-id="element-149">
                      <img src={slides[currentSlide].image} alt={slides[currentSlide].title || 'Andrew Igbineweka'} className="w-full h-full object-cover filter grayscale" data-id="element-150" />
                    </motion.div>
                  </div>
                </> : <>
                  <div className="w-full md:w-1/2 h-full relative order-2 md:order-1" data-id="element-151">
                    <motion.div className="absolute inset-0 bg-gradient-to-l from-gray-50 to-transparent z-10 md:block hidden" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  duration: 0.8
                }} data-id="element-152" />
                    <motion.div className="w-full h-full" initial={{
                  scale: 1.1
                }} animate={{
                  scale: 1
                }} transition={{
                  duration: 1.5
                }} data-id="element-153">
                      <img src={slides[currentSlide].image} alt={slides[currentSlide].title || 'Andrew Igbineweka'} className="w-full h-full object-cover filter grayscale" data-id="element-154" />
                    </motion.div>
                  </div>
                  <div className="w-full md:w-1/2 h-full flex items-center justify-center p-8 md:p-16 order-1 md:order-2" data-id="element-155">
                    <div className="max-w-xl" data-id="element-156">
                      {slides[currentSlide].title && <motion.h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-gray-900" initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.6,
                    delay: 0.2
                  }} data-id="element-157">
                          {slides[currentSlide].title}
                        </motion.h1>}
                      <motion.p className="text-xl md:text-2xl lg:text-3xl font-light mb-8 text-gray-800 leading-relaxed" initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.6,
                    delay: 0.4
                  }} data-id="element-158">
                        {slides[currentSlide].content}
                      </motion.p>
                      {slides[currentSlide].tools && renderTools(slides[currentSlide].tools)}
                      <SocialButtons data-id="element-159" />
                    </div>
                  </div>
                </>}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      {/* Slide Navigation */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2" data-id="element-160">
        {slides.map((_, index) => <button key={index} onClick={() => {
        setDirection(index > currentSlide ? 1 : -1);
        setCurrentSlide(index);
        pauseAutoPlay();
        setTimeout(resumeAutoPlay, 5000);
      }} className={`w-3 h-3 rounded-full transition-all duration-300 ${currentSlide === index ? 'bg-gray-800 w-8' : 'bg-gray-400'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-161" />)}
      </div>
      {/* Arrow Navigation */}
      <button className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/80 backdrop-blur-sm p-2 rounded-full shadow-md text-gray-800 hover:bg-white transition-all duration-300" onClick={() => {
      prevSlide();
      pauseAutoPlay();
      setTimeout(resumeAutoPlay, 5000);
    }} aria-label="Previous slide" data-id="element-162">
        <ChevronLeft size={24} data-id="element-163" />
      </button>
      <button className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/80 backdrop-blur-sm p-2 rounded-full shadow-md text-gray-800 hover:bg-white transition-all duration-300" onClick={() => {
      nextSlide();
      pauseAutoPlay();
      setTimeout(resumeAutoPlay, 5000);
    }} aria-label="Next slide" data-id="element-164">
        <ChevronRight size={24} data-id="element-165" />
      </button>
    </section>;
}